import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Lock, Building2, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

export default function LoginPage() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      dealerCode: 'dlr',
      password: '',
      role: 'Dealer Service Manager',
      rememberMe: true,
    },
  });
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const [user, setUser] = useState(() => {
    const saved = sessionStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [isLoggedIn, setIsLoggedIn] = useState(Boolean(user));

  const onLogin = (userData) => {
    setUser(userData);
    setIsLoggedIn(true);
    sessionStorage.setItem('user', JSON.stringify(userData));
    navigate('/group/grouplist', { replace: true });
  };

  useEffect(() => {
    if (!isLoggedIn && user) {
      setIsLoggedIn(true);
    }
  }, [user, isLoggedIn]);

  const onSubmit = async (data) => {
    const username = data.dealerCode.trim();
    const password = data.password.trim();

    if (!username || !password) {
      toast.error('Please enter both Username and password');
      return;
    }

    const isValidUser = username.toLowerCase() === "dlrmim";
    const isValidPassword = password.toLowerCase() === "mim4082";

    if (!isValidUser || !isValidPassword) {
      toast.error('Invalid credentials. Please try again.');
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 550));
    toast.success(`Welcome back, ${data.role}!`);

    onLogin({
      dealerCode: data.dealerCode,
      role: data.role,
      name: data.dealerCode === 'DL-4082' ? 'Hyundai Apex Motors' : `User ${data.dealerCode}`,
      email: `${data.dealerCode.toLowerCase()}@hyundai-dealer.in`,
      loginTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    });
  };


  return (
    <div className="min-h-screen bg-white text-slate-100 flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-hidden font-sans">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-[#00AAD2]/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-[#002C6C]/40 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#001E4A]/30 rounded-full blur-[120px]" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: `radial-gradient(#00AAD2 1px, transparent 1px)`, backgroundSize: '24px 24px' }}
        />
      </div>

      <div className="w-full max-w-lg bg-slate-900/90 border border-slate-800 rounded-[28px] shadow-2xl backdrop-blur-2xl overflow-hidden relative z-10">
        <div className="p-8 lg:p-10 flex items-center justify-center">
          <div className="w-full max-w-md">
            <div className="mb-8 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-400/90">Dealer Sign In</p>
              <h2 className="mt-3 text-3xl font-bold text-white">Hyundai Motors India Limited</h2>
              <p className="mt-2 text-sm text-slate-400">Use your Hyundai dealer credentials to continue.</p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label htmlFor="dealerCode" className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-300 mb-2">
                  Username
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-cyan-300/80">
                    <Building2 className="w-4 h-4" />
                  </div>
                  <input
                    id="dealerCode"
                    type="text"
                    {...register('dealerCode', { required: 'Dealer Code is required' })}
                    placeholder="DL-4082"
                    className="w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-12 py-3 text-sm text-white outline-none transition focus:border-cyan-300 focus:ring-2 focus:ring-cyan-300/25"
                  />
                </div>
                {errors.dealerCode && <p className="mt-2 text-xs text-rose-300">{errors.dealerCode.message}</p>}
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-cyan-300/80">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', { required: 'Password is required' })}
                    placeholder="••••••••"
                    className="w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-12 py-3 text-sm text-white outline-none transition focus:border-cyan-300 focus:ring-2 focus:ring-cyan-300/25"
                  />
                  <div onClick={() => setShowPassword((prev) => !prev)} className="cursor-pointer absolute inset-y-0 right-4 flex items-center pl-3 text-cyan-300/80">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </div>
                </div>
                {errors.password && <p className="mt-2 text-xs text-rose-300">{errors.password.message}</p>}
              </div>

              <div className="flex items-center justify-between text-sm text-slate-400">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register('rememberMe')}
                    className="h-4 w-4 rounded border-slate-700 bg-slate-950 text-cyan-400 focus:ring-cyan-400"
                  />
                  Remember me
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-2xl bg-gradient-to-r from-[#002C6C] to-[#00AAD2] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-[#002C6C]/30 transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {isSubmitting ? 'Signing in...' : 'Sign in to Hyundai Portal'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
